<template>
  <b-card>
    <validation-observer v-slot="{ invalid }">
      <!-- form -->
      <b-form class="mt-2">
        <b-row>
          <b-col sm="12">
            <b-form-group>
              <b-media class="mb-0">
                <template #aside>
                  <b-avatar
                    :src="entity.participant.profilePhoto"
                    :text="avatarText(entity.participant.firstName)"
                    size="120px"
                    rounded
                  />
                </template>
                <h4 class="mb-1">
                  {{
                    entity.participant.firstName +
                    ' ' +
                    entity.participant.lastName
                  }}
                </h4>
                <div class="d-flex flex-wrap">
                  <b-button
                    variant="primary"
                    @click="$refs.inputFile.click()"
                  >
                    <input
                      id="user-avatar"
                      ref="inputFile"
                      type="file"
                      accept="image/*"
                      class="d-none"
                      @input="handleImageChange($refs.inputFile)"
                    />
                    <span>{{
                      entity.participant.profilePhoto ? $t('Update') : $t('Add')
                    }}</span>
                  </b-button>
                  <b-button
                    v-if="entity.participant.profilePhoto"
                    variant="outline-secondary"
                    class="ml-1"
                    @click="handleImageChange(null)"
                  >
                    <span>{{ $t('Delete') }}</span>
                  </b-button>
                </div>
              </b-media>
            </b-form-group>
          </b-col>
          <EntityEditSection
            :entity="entity"
            :form-fields="fields"
            module=""
            allow-autofocus
          />
          <b-col cols="12">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mt-0 mr-1"
              :disabled="invalid"
              @click.prevent="submitForm"
            >
              {{ $t('Save Changes') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              type="reset"
              class="mt-0"
              @click.prevent="reloadData"
            >
              {{ $t('Reset') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-card>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BRow,
  BCol,
  BCard,
  BAvatar,
  BMedia,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import Ripple from 'vue-ripple-directive'
import { useInputImageRenderer } from '@core/comp-functions/forms/form-utils'
import EntityEditSection from '@/layouts/entity/EntityEditSection.vue'
import { ref } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BRow,
    BCol,
    BCard,
    BAvatar,
    BMedia,
    ValidationObserver,
    EntityEditSection
  },
  directives: {
    Ripple,
  },
  props: {
    userData: {
      type: Object,
      default: () => { },
    },
  },
  setup() {
    const refInputEl = ref(null)
    const previewEl = ref(null)

    const { inputImageRenderer } = useInputImageRenderer(refInputEl, previewEl)

    return {
      refInputEl,
      previewEl,
      inputImageRenderer,
      avatarText,
    }
  },
  data() {
    return {
      entity: JSON.parse(JSON.stringify(this.userData)),
      professionOptions: [],
      specialtiesOptions: [],
      workingGroupOptions: [],
      genderOptions: [
        { id: 1, title: 'Άνδρας' },
        { id: 2, title: 'Γυναίκα' },
      ],
    }
  },
  computed: {
    fields() {
      return [
        {
          id: 'memberCode',
          label: this.$t('Member Code'),
          readonly: true,
        },
        {
          id: 'firstName',
          label: this.$t('First Name'),
          rules: 'min:3',
        },
        {
          id: 'lastName',
          label: this.$t('Last Name'),
          rules: 'min:3',
        },
        {
          id: 'email',
          type: 'email',
          label: this.$t('email'),
          rules: 'email',
        },
        {
          id: 'birthDate',
          type: 'date',
          label: this.$t('birth date'),
          maxDate: new Date(),
          optional: true
        },
        {
          id: 'profession',
          object: true,
          type: 'select',
          label: this.$t('Profession'),
          options: this.professionOptions,
          optional: true
        },
        {
          id: 'specialty',
          object: true,
          type: 'select',
          label: this.$t('Specialty'),
          readonly: true,
          options: this.specialtiesOptions,
        },
        {
          id: 'specialtyYear',
          type: 'number',
          label: this.$t('specialtyYear'),
          rules: 'min:4|max:4|positive',
          readonly: true,
          colSize: 3,
        },
        {
          id: 'gender',
          type: 'select',
          label: this.$t('gender'),
          options: this.genderOptions,
          colSize: 3,
        },
        {
          id: 'workingGroups',
          object: true,
          noParent: true,
          type: 'selectmultiple',
          label: this.$t('Working Groups'),
          options: this.workingGroupOptions,
          readonly: true,
          colSize: 12,
        },
      ].map(field => ({
        ...field,
        type: field.type || 'text',
        parent: field.noParent ? null : 'participant',
        class: 'mt-0',
        required: !field.optional && !field.readonly,
        colSize: field.colSize || 6,
      }))
    },
  },
  mounted() {
    this.$store.dispatch('app-common/fetchProfessions').then((response) => {
      this.professionOptions = response.data
    })
    this.$store.dispatch('app-common/fetchSpecialties').then((response) => {
      this.specialtiesOptions = response.data
    })
    this.$store.dispatch('app-common/fetchWorkingGroups').then((response) => {
      this.workingGroupOptions = response.data
    })
  },
  methods: {
    reloadData() {
      this.entity = JSON.parse(JSON.stringify(this.userData))
    },
    submitForm() {
      this.$emit('formSubmitted', {
        ...this.entity,
      })
    },
    async handleImageChange(input) {
      const file = input && input.files && input.files[0]

      if (file) {
        this.entity.participant.profilePhoto = await this.blobToBase64(file)
      } else {
        this.entity.participant.profilePhoto = null
      }
    },
  },
}
</script>
